import type { FormEvent, JSX, ReactNode } from 'react';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Badge, Button, Checkbox, Dialog, Input, Select, VisuallyHidden } from '@artosera/ui';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import {
  analyserEntier,
  analyserMontant,
  analyserPourcentage,
  montantEnSaisie,
  pourcentageEnSaisie,
} from '../../lib/montant.js';
import { TableauDefilant } from '../../components/TableauDefilant.js';
import tableStyles from '../../styles/table.module.css';
import styles from './TableauSaisie.module.css';

/**
 * Tableau de saisie ligne à ligne (ARTO-58), commun aux onglets de
 * `/finances`, à `/jalons` et au partage de `/associes`.
 *
 * Règles de la spec (§ 2) que ce composant tient pour tous les écrans :
 *
 *  - un champ montant vide envoie `null`, jamais zéro ; aucun montant n'est
 *    pré-rempli (seul `proposer` peut en suggérer un, à la création, et la
 *    suggestion reste modifiable et effaçable) ;
 *  - une ligne sans droit d'écriture se lit, rien de plus : ni bouton ni champ ;
 *  - une modification n'envoie que les champs changés ;
 *  - l'erreur de l'API s'affiche sur la ligne concernée, l'éditeur reste ouvert.
 */

export type TypeChamp =
  'texte' | 'mois' | 'date' | 'montant' | 'entier' | 'pourcentage' | 'choix' | 'cases' | 'booleen';

/** Valeur typée d'un champ, telle que l'API la lit et l'écrit. */
export type ValeurChamp = string | number | boolean | null;
/** L'état d'un formulaire en cours : le TEXTE de chaque champ (case : booléen). */
export type Brouillon = Record<string, string | boolean>;

export interface OptionChoix {
  value: string;
  label: string;
}

export interface ChampSaisie {
  /** Clé du champ dans le brouillon et dans `valeursDe`. */
  cle: string;
  /** Nom du champ dans le corps de la requête, s'il diffère de `cle`. */
  cleCorps?: string;
  titre: string;
  type: TypeChamp;
  options?: readonly OptionChoix[];
  /**
   * Les options d'un champ `cases` ou `choix` qui dépendent d'un autre champ
   * (les galeries d'un exploitant) : lues dans le brouillon courant.
   */
  optionsDe?: (brouillon: Brouillon) => readonly OptionChoix[];
  /** Ce que dit un champ `cases` sans aucune option. */
  messageSansOption?: string;
  /** Un choix facultatif : le libellé de l'option « aucun », qui envoie `null`. */
  optionVide?: string;
  requis?: boolean;
  longueurMax?: number;
  /** Texte d'aide sous le champ, relié par `aria-describedby`. */
  aide?: string;
  /** Un champ numérique non nullable : ce qu'il envoie quand il est vide. */
  videVaut?: number;
  /** Absent de l'éditeur d'une ligne existante (la galerie d'un abonnement). */
  creationSeule?: boolean;
  /** Valeur de départ d'un champ qui n'est pas un montant (jamais un montant). */
  initiale?: string | boolean;
  visible?: (brouillon: Brouillon) => boolean;
  /** Met la valeur typée en forme pour le corps (`galeries: [id]`). */
  enCorps?: (valeur: ValeurChamp) => unknown;
  pleineLargeur?: boolean;
}

export interface ColonneSaisie<L> {
  cle: string;
  titre: string;
  rendu: (ligne: L) => ReactNode;
  numerique?: boolean;
}

export interface TableauSaisieProps<L extends { id: string }> {
  legende: string;
  lignes: readonly L[];
  colonnes: readonly ColonneSaisie<L>[];
  /**
   * Les champs de l'éditeur. Une fonction les choisit selon la ligne (`null` :
   * une création) : le forfait d'un exploitant n'a ni galerie ni palier.
   */
  champs: readonly ChampSaisie[] | ((ligne: L | null) => readonly ChampSaisie[]);
  /** Les valeurs typées d'une ligne, par `cle` de champ. */
  valeursDe: (ligne: L) => Record<string, ValeurChamp>;
  /** Le nom de la ligne pour les lecteurs d'écran (« Modifier Hébergement »). */
  libelleLigne: (ligne: L) => string;
  /** Droit d'écriture de l'onglet : faux = lecture seule. */
  enEcriture: boolean;
  libelleAjouter: string;
  messageVide: string;
  /** Texte d'introduction de l'onglet. */
  introduction?: string;
  /** Champs que tout corps de création porte en plus (`nature: 'galerie'`). */
  corpsFixe?: Record<string, unknown>;
  /** Lignes dont un total ignore le montant : le tableau le dit. */
  sansMontant?: number;
  /**
   * Suggestions à la création : appelée à chaque changement d'un champ, rend
   * les textes à poser dans d'AUTRES champs. Un champ que la main a touché
   * n'est plus jamais écrasé.
   */
  proposer?: (cle: string, valeur: string | boolean, brouillon: Brouillon) => Brouillon;
  /**
   * Champs qui dépendent d'un autre : changer l'exploitant remet ses galeries à
   * zéro. Contrairement à `proposer`, ces valeurs s'imposent toujours, qu'on
   * ait touché le champ ou non.
   */
  surChangement?: (cle: string, valeur: string | boolean, brouillon: Brouillon) => Brouillon;
  surCreer?: (corps: Record<string, unknown>) => Promise<unknown>;
  /** `changements` : les seuls champs modifiés ; `complet` : tous, pour une première saisie. */
  surModifier?: (
    ligne: L,
    changements: Record<string, unknown>,
    complet: Record<string, unknown>,
  ) => Promise<unknown>;
  surSupprimer?: (ligne: L) => Promise<unknown>;
  /** Peut-on supprimer cette ligne ? (un bénéficiaire sans versement…). */
  suppressionPossible?: (ligne: L) => boolean;
  /** Peut-on modifier cette ligne ? (pas de versement à créer dans un mois clôturé…). */
  modificationPossible?: (ligne: L) => boolean;
  /** Le texte du bouton d'une ligne : « Saisir » pour un versement qui n'existe pas encore. */
  libelleModifier?: (ligne: L) => string;
  /** Message lisible d'une erreur de l'API. */
  message: (erreur: unknown) => string;
  /** Un pied de tableau libre (total des parts…). */
  pied?: ReactNode;
}

const AUCUN = '__aucun__';

// ── Lecture d'un champ ───────────────────────────────────────────────────

type CleErreurChamp =
  'requis' | 'montant' | 'entier' | 'pourcentage' | 'date' | 'mois' | 'longueur';

type Lecture = { ok: true; valeur: ValeurChamp } | { ok: false; erreur: CleErreurChamp };

function lireChamp(champ: ChampSaisie, texte: string | boolean): Lecture {
  if (champ.type === 'booleen') return { ok: true, valeur: texte === true };
  const brut = typeof texte === 'string' ? texte : '';
  const vide = brut.trim() === '';
  const requis = (): Lecture => ({ ok: false, erreur: 'requis' });
  const absent = (): Lecture =>
    champ.requis ? requis() : { ok: true, valeur: champ.videVaut ?? null };

  switch (champ.type) {
    case 'texte': {
      if (vide) return champ.requis ? requis() : { ok: true, valeur: null };
      const valeur = brut.trim();
      if (champ.longueurMax !== undefined && valeur.length > champ.longueurMax) {
        return { ok: false, erreur: 'longueur' };
      }
      return { ok: true, valeur };
    }
    case 'choix':
      return vide
        ? champ.requis
          ? requis()
          : { ok: true, valeur: null }
        : { ok: true, valeur: brut };
    case 'cases':
      return vide
        ? champ.requis
          ? requis()
          : { ok: true, valeur: null }
        : { ok: true, valeur: brut };
    case 'mois':
      if (vide) return absent();
      return /^\d{4}-(0[1-9]|1[0-2])$/.test(brut.trim())
        ? { ok: true, valeur: brut.trim() }
        : { ok: false, erreur: 'mois' };
    case 'date':
      if (vide) return absent();
      return /^\d{4}-\d{2}-\d{2}$/.test(brut.trim())
        ? { ok: true, valeur: brut.trim() }
        : { ok: false, erreur: 'date' };
    case 'montant':
    case 'entier':
    case 'pourcentage': {
      const lecture =
        champ.type === 'montant'
          ? analyserMontant(brut)
          : champ.type === 'entier'
            ? analyserEntier(brut)
            : analyserPourcentage(brut);
      if (!lecture.ok) return { ok: false, erreur: champ.type };
      return lecture.valeur === null ? absent() : { ok: true, valeur: lecture.valeur };
    }
  }
}

/** La valeur typée d'une ligne existante, mise en texte pour le champ. */
function enTexte(champ: ChampSaisie, valeur: ValeurChamp | undefined): string | boolean {
  if (champ.type === 'booleen') return valeur === true;
  if (valeur === null || valeur === undefined) return '';
  // Un champ vide vaut `videVaut` (versé, encaissé, remise, ordre : zéro) : on
  // ne pré-remplit pas ce zéro, le champ se lit vide.
  if (champ.videVaut !== undefined && valeur === champ.videVaut) return '';
  switch (champ.type) {
    case 'montant':
      return montantEnSaisie(Number(valeur));
    case 'pourcentage':
      return pourcentageEnSaisie(Number(valeur));
    case 'date':
      return String(valeur).slice(0, 10);
    default:
      return String(valeur);
  }
}

/** La valeur d'une ligne existante, ramenée à la forme que `lireChamp` produit. */
function normalisee(champ: ChampSaisie, valeur: ValeurChamp | undefined): ValeurChamp {
  if (valeur === undefined) return champ.type === 'booleen' ? false : null;
  if (champ.type === 'date' && typeof valeur === 'string') return valeur.slice(0, 10);
  return valeur;
}

interface Edition {
  /** `null` : une nouvelle ligne. */
  id: string | null;
  brouillon: Brouillon;
  /** Champs que la main a touchés : une suggestion ne les écrase plus. */
  touches: ReadonlySet<string>;
  erreursChamp: Record<string, CleErreurChamp>;
  erreur: string | null;
  enCours: boolean;
}

export function TableauSaisie<L extends { id: string }>(props: TableauSaisieProps<L>): JSX.Element {
  const {
    legende,
    lignes,
    colonnes,
    champs: definitionChamps,
    valeursDe,
    libelleLigne,
    enEcriture,
    libelleAjouter,
    messageVide,
    introduction,
    corpsFixe,
    sansMontant = 0,
    proposer,
    surChangement,
    surCreer,
    surModifier,
    surSupprimer,
    suppressionPossible,
    modificationPossible,
    libelleModifier,
    message,
    pied,
  } = props;
  const { t } = useTranslation('console');
  const prefixe = useId();
  const [edition, setEdition] = useState<Edition | null>(null);
  const [erreursLigne, setErreursLigne] = useState<Record<string, string>>({});
  const [aSupprimer, setASupprimer] = useState<L | null>(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  // Une suppression demande un second facteur récent : la boîte s'ouvre si
  // l'API le demande, puis la suppression est rejouée.
  const elevation = useElevation();
  const declencheur = useRef<HTMLElement | null>(null);
  const formulaire = useRef<HTMLFormElement | null>(null);
  const nombreColonnes = colonnes.length + (enEcriture ? 1 : 0);

  // L'éditeur s'ouvre sur son premier champ : on saisit tout de suite.
  const ouvert = edition !== null;
  const identifiantOuvert = edition?.id;
  useEffect(() => {
    if (!ouvert) return;
    const premier = formulaire.current?.querySelector<HTMLElement>(
      'input:not([type=hidden]), button[role=combobox]',
    );
    premier?.focus();
  }, [ouvert, identifiantOuvert]);

  const idChamp = (cle: string): string => `${prefixe}-${cle}`;
  const champsDe = (ligne: L | null): readonly ChampSaisie[] =>
    typeof definitionChamps === 'function' ? definitionChamps(ligne) : definitionChamps;

  function brouillonDe(ligne: L | null): Brouillon {
    const valeurs = ligne ? valeursDe(ligne) : null;
    const resultat: Brouillon = {};
    for (const champ of champsDe(ligne)) {
      resultat[champ.cle] = valeurs
        ? enTexte(champ, valeurs[champ.cle])
        : (champ.initiale ?? (champ.type === 'booleen' ? false : ''));
    }
    return resultat;
  }

  function ouvrir(ligne: L | null, depuis: HTMLElement | null): void {
    declencheur.current = depuis;
    setEdition({
      id: ligne ? ligne.id : null,
      brouillon: brouillonDe(ligne),
      touches: new Set(),
      erreursChamp: {},
      erreur: null,
      enCours: false,
    });
    if (ligne) setErreursLigne(({ [ligne.id]: _retiree, ...reste }) => reste);
  }

  function fermer(): void {
    setEdition(null);
    const cible = declencheur.current;
    declencheur.current = null;
    // Le focus revient où l'on était : le bouton qui a ouvert l'éditeur.
    queueMicrotask(() => {
      if (cible?.isConnected) cible.focus();
    });
  }

  function changer(cle: string, valeur: string | boolean): void {
    setEdition((courant) => {
      if (!courant) return courant;
      const touches = new Set(courant.touches).add(cle);
      let brouillon: Brouillon = { ...courant.brouillon, [cle]: valeur };
      if (courant.id === null && surChangement) {
        brouillon = { ...brouillon, ...surChangement(cle, valeur, brouillon) };
      }
      if (courant.id === null && proposer) {
        const suggestions = proposer(cle, valeur, brouillon);
        for (const [autre, texte] of Object.entries(suggestions)) {
          if (!touches.has(autre)) brouillon = { ...brouillon, [autre]: texte };
        }
      }
      const { [cle]: _corrigee, ...erreursChamp } = courant.erreursChamp;
      return { ...courant, brouillon, touches, erreursChamp, erreur: null };
    });
  }

  const visible = (champ: ChampSaisie, brouillon: Brouillon): boolean =>
    champ.visible ? champ.visible(brouillon) : true;

  async function enregistrer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    if (!edition) return;
    const ligne = edition.id === null ? null : (lignes.find((l) => l.id === edition.id) ?? null);
    const creation = ligne === null;
    const valeursAvant = ligne ? valeursDe(ligne) : {};
    const champs = champsDe(ligne);

    const erreursChamp: Record<string, CleErreurChamp> = {};
    const complet: Record<string, unknown> = creation ? { ...corpsFixe } : {};
    const changements: Record<string, unknown> = {};
    for (const champ of champs) {
      if (!creation && champ.creationSeule) continue;
      const cible = champ.cleCorps ?? champ.cle;
      const forme = (valeur: ValeurChamp): unknown =>
        champ.enCorps ? champ.enCorps(valeur) : valeur;
      if (!visible(champ, edition.brouillon)) {
        // Un champ masqué (le palier d'un forfait) n'a plus de valeur : `null`.
        // À la création il n'est pas envoyé du tout.
        if (!creation && normalisee(champ, valeursAvant[champ.cle]) !== null) {
          changements[cible] = null;
          complet[cible] = null;
        }
        continue;
      }
      const lecture = lireChamp(champ, edition.brouillon[champ.cle] ?? '');
      if (!lecture.ok) {
        erreursChamp[champ.cle] = lecture.erreur;
        continue;
      }
      complet[cible] = forme(lecture.valeur);
      if (!creation && lecture.valeur !== normalisee(champ, valeursAvant[champ.cle])) {
        changements[cible] = forme(lecture.valeur);
      }
    }
    if (Object.keys(erreursChamp).length > 0) {
      setEdition({ ...edition, erreursChamp, erreur: null });
      return;
    }
    if (!creation && Object.keys(changements).length === 0) {
      fermer();
      return;
    }

    setEdition({ ...edition, erreursChamp: {}, erreur: null, enCours: true });
    try {
      if (creation) await surCreer?.(complet);
      else await surModifier?.(ligne, changements, complet);
      fermer();
    } catch (erreur) {
      if (estElevationAnnulee(erreur)) {
        setEdition((courant) => (courant ? { ...courant, enCours: false } : courant));
        return;
      }
      setEdition((courant) =>
        courant ? { ...courant, enCours: false, erreur: message(erreur) } : courant,
      );
    }
  }

  async function supprimer(): Promise<void> {
    const ligne = aSupprimer;
    if (!ligne || !surSupprimer) return;
    setSuppressionEnCours(true);
    try {
      await elevation.executer(() => surSupprimer(ligne));
      setErreursLigne(({ [ligne.id]: _retiree, ...reste }) => reste);
    } catch (erreur) {
      if (!estElevationAnnulee(erreur)) {
        setErreursLigne((courant) => ({ ...courant, [ligne.id]: message(erreur) }));
      }
    } finally {
      setSuppressionEnCours(false);
      setASupprimer(null);
    }
  }

  // ── Rendu ────────────────────────────────────────────────────────────────

  function rendreChamp(champ: ChampSaisie, courante: Edition): JSX.Element {
    const id = idChamp(champ.cle);
    const idAide = `${id}-aide`;
    const idErreur = `${id}-erreur`;
    const valeur = courante.brouillon[champ.cle] ?? '';
    const erreur = courante.erreursChamp[champ.cle];
    const decrit = [champ.aide ? idAide : null, erreur ? idErreur : null].filter(Boolean).join(' ');
    const classes = [styles.champ, champ.pleineLargeur ? styles.pleineLargeur : '']
      .filter(Boolean)
      .join(' ');
    const texte = typeof valeur === 'string' ? valeur : '';

    let controle: JSX.Element;
    switch (champ.type) {
      case 'booleen':
        controle = (
          <Checkbox
            id={id}
            label={champ.titre}
            checked={valeur === true}
            onCheckedChange={(coche) => changer(champ.cle, coche === true)}
            aria-describedby={decrit || undefined}
          />
        );
        break;
      case 'cases': {
        const options = champ.optionsDe
          ? champ.optionsDe(courante.brouillon)
          : (champ.options ?? []);
        const cochees = new Set(texte === '' ? [] : texte.split(','));
        controle = (
          <fieldset
            className={styles.cases}
            aria-describedby={decrit || undefined}
            aria-invalid={erreur ? true : undefined}
          >
            <legend>{champ.titre}</legend>
            {options.length === 0 ? (
              <p className={styles.aide}>{champ.messageSansOption}</p>
            ) : (
              options.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`${id}-${option.value}`}
                  label={option.label}
                  checked={cochees.has(option.value)}
                  onCheckedChange={(coche) => {
                    const suivantes = new Set(cochees);
                    if (coche === true) suivantes.add(option.value);
                    else suivantes.delete(option.value);
                    // L'ordre des options, pas celui des clics.
                    changer(
                      champ.cle,
                      options
                        .map((candidate) => candidate.value)
                        .filter((valeurOption) => suivantes.has(valeurOption))
                        .join(','),
                    );
                  }}
                />
              ))
            )}
          </fieldset>
        );
        break;
      }
      case 'choix': {
        const base = champ.optionsDe ? champ.optionsDe(courante.brouillon) : (champ.options ?? []);
        const options = champ.optionVide
          ? [{ value: AUCUN, label: champ.optionVide }, ...base]
          : base;
        controle = (
          <Select
            id={id}
            options={options}
            value={champ.optionVide && texte === '' ? AUCUN : texte}
            placeholder={t('saisies.choisir')}
            onValueChange={(choisie) => changer(champ.cle, choisie === AUCUN ? '' : choisie)}
            aria-describedby={decrit || undefined}
          />
        );
        break;
      }
      case 'montant':
      case 'pourcentage':
        controle = (
          <span className={styles.avecSuffixe}>
            <Input
              id={id}
              mono
              inputMode="decimal"
              autoComplete="off"
              placeholder={
                champ.videVaut !== undefined
                  ? String(champ.videVaut)
                  : champ.type === 'montant'
                    ? t('saisies.aSaisir')
                    : undefined
              }
              value={texte}
              onChange={(e) => changer(champ.cle, e.target.value)}
              aria-invalid={erreur ? true : undefined}
              aria-describedby={decrit || undefined}
            />
            <span className={styles.suffixe} aria-hidden="true">
              {champ.type === 'montant' ? '€' : '%'}
            </span>
          </span>
        );
        break;
      case 'entier':
        controle = (
          <Input
            id={id}
            mono
            inputMode="numeric"
            autoComplete="off"
            placeholder={champ.videVaut !== undefined ? String(champ.videVaut) : undefined}
            value={texte}
            onChange={(e) => changer(champ.cle, e.target.value)}
            aria-invalid={erreur ? true : undefined}
            aria-describedby={decrit || undefined}
          />
        );
        break;
      case 'mois':
      case 'date':
        controle = (
          <Input
            id={id}
            mono
            type={champ.type === 'mois' ? 'month' : 'date'}
            placeholder={champ.type === 'mois' ? 'AAAA-MM' : undefined}
            value={texte}
            onChange={(e) => changer(champ.cle, e.target.value)}
            aria-invalid={erreur ? true : undefined}
            aria-describedby={decrit || undefined}
          />
        );
        break;
      default:
        controle = (
          <Input
            id={id}
            autoComplete="off"
            maxLength={champ.longueurMax}
            value={texte}
            onChange={(e) => changer(champ.cle, e.target.value)}
            aria-invalid={erreur ? true : undefined}
            aria-describedby={decrit || undefined}
          />
        );
    }

    return (
      <div key={champ.cle} className={classes}>
        {champ.type === 'booleen' || champ.type === 'cases' ? null : (
          <label htmlFor={id}>{champ.titre}</label>
        )}
        {controle}
        {champ.aide ? (
          <p id={idAide} className={styles.aide}>
            {champ.aide}
          </p>
        ) : null}
        {erreur ? (
          <p id={idErreur} className={styles.erreurChamp}>
            {t(`saisies.erreursChamp.${erreur}`, { max: champ.longueurMax })}
          </p>
        ) : null}
      </div>
    );
  }

  function rendreEditeur(courante: Edition): JSX.Element {
    const creation = courante.id === null;
    const existante = lignes.find((candidate) => candidate.id === courante.id);
    const champs = champsDe(existante ?? null);
    const titre = existante
      ? t('saisies.modifierLigne', { ligne: libelleLigne(existante) })
      : t('saisies.nouvelleLigne');
    return (
      <tr className={styles.ligneEditeur}>
        <td colSpan={nombreColonnes}>
          <form
            ref={formulaire}
            className={styles.editeur}
            aria-label={titre}
            onSubmit={(e) => void enregistrer(e)}
            noValidate
          >
            <div className={styles.champs}>
              {champs
                .filter(
                  (champ) =>
                    (creation || !champ.creationSeule) && visible(champ, courante.brouillon),
                )
                .map((champ) => rendreChamp(champ, courante))}
            </div>
            {courante.erreur ? (
              <p className={styles.erreurLigne} role="alert">
                {courante.erreur}
              </p>
            ) : null}
            <div className={styles.actionsEditeur}>
              <Button type="submit" loading={courante.enCours}>
                {courante.enCours ? t('saisies.enregistrement') : t('saisies.enregistrer')}
              </Button>
              <Button variant="ghost" onClick={fermer} disabled={courante.enCours}>
                {t('saisies.annuler')}
              </Button>
            </div>
          </form>
        </td>
      </tr>
    );
  }

  const peutAjouter = enEcriture && surCreer !== undefined;
  const creationOuverte = edition?.id === null;

  return (
    <div className={styles.conteneur}>
      <div className={styles.barre}>
        {/* Sans texte à dire, pas de bloc : un bloc vide gardait sa gouttière et
            décalait le bouton « Ajouter » de 16 px à 390 px. */}
        {introduction || !enEcriture ? (
          <div className={styles.introduction}>
            {introduction ? <p>{introduction}</p> : null}
            {!enEcriture ? (
              <p className={styles.lectureSeule}>
                <Badge>
                  <Lock size={12} strokeWidth={1.75} aria-hidden="true" />
                  {t('saisies.lectureSeule')}
                </Badge>
                <span>{t('saisies.lectureSeuleAide')}</span>
              </p>
            ) : null}
          </div>
        ) : null}
        {peutAjouter ? (
          <Button
            className={styles.ajouter}
            onClick={(e) => ouvrir(null, e.currentTarget)}
            disabled={creationOuverte}
          >
            {libelleAjouter}
          </Button>
        ) : null}
      </div>

      <TableauDefilant empile>
        <table className={`${tableStyles.table} ${styles.empile}`} aria-label={legende}>
          <thead>
            <tr>
              {colonnes.map((colonne) => (
                <th
                  key={colonne.cle}
                  scope="col"
                  className={colonne.numerique ? styles.num : undefined}
                >
                  {colonne.titre}
                </th>
              ))}
              {enEcriture ? (
                <th scope="col">
                  <VisuallyHidden>{t('saisies.actions')}</VisuallyHidden>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {creationOuverte && edition ? rendreEditeur(edition) : null}
            {lignes.length === 0 && !creationOuverte ? (
              <tr>
                <td colSpan={nombreColonnes} className={styles.videEtat}>
                  {messageVide}
                </td>
              </tr>
            ) : null}
            {lignes.map((ligne) => (
              <Fragment key={ligne.id}>
                {edition?.id === ligne.id ? (
                  rendreEditeur(edition)
                ) : (
                  <tr>
                    {colonnes.map((colonne, rang) => (
                      <td
                        key={colonne.cle}
                        data-label={colonne.titre}
                        className={[
                          colonne.numerique ? styles.num : '',
                          rang === 0 ? tableStyles.cellulePrincipale : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {colonne.rendu(ligne)}
                      </td>
                    ))}
                    {enEcriture ? (
                      <td className={styles.celluleActions}>
                        <div className={styles.actionsLigne}>
                          {surModifier && (modificationPossible?.(ligne) ?? true) ? (
                            <Button
                              variant="secondary"
                              disabled={edition !== null}
                              aria-label={
                                libelleModifier
                                  ? `${libelleModifier(ligne)} ${libelleLigne(ligne)}`
                                  : t('saisies.modifierLigne', { ligne: libelleLigne(ligne) })
                              }
                              onClick={(e) => ouvrir(ligne, e.currentTarget)}
                            >
                              {libelleModifier ? libelleModifier(ligne) : t('saisies.modifier')}
                            </Button>
                          ) : null}
                          {surSupprimer && (suppressionPossible?.(ligne) ?? true) ? (
                            <Button
                              variant="danger"
                              disabled={edition !== null}
                              aria-label={t('saisies.supprimerLigne', {
                                ligne: libelleLigne(ligne),
                              })}
                              onClick={() => setASupprimer(ligne)}
                            >
                              {t('saisies.supprimer')}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                )}
                {erreursLigne[ligne.id] ? (
                  <tr className={styles.ligneErreur}>
                    <td colSpan={nombreColonnes}>
                      <p className={styles.erreurLigne} role="alert">
                        {erreursLigne[ligne.id]}
                      </p>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </TableauDefilant>

      {sansMontant > 0 ? (
        <p className={styles.resume}>{t('saisies.sansMontant', { count: sansMontant })}</p>
      ) : null}
      {pied}

      <Dialog
        open={aSupprimer !== null && !elevation.enElevation}
        onOpenChange={(ouvertDialogue) => {
          if (!ouvertDialogue && !suppressionEnCours) setASupprimer(null);
        }}
        title={t('saisies.suppression.titre')}
        description={
          aSupprimer
            ? t('saisies.suppression.texte', { ligne: libelleLigne(aSupprimer) })
            : undefined
        }
        closeLabel={t('saisies.suppression.fermer')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setASupprimer(null)}
              disabled={suppressionEnCours}
            >
              {t('saisies.annuler')}
            </Button>
            <Button variant="danger" onClick={() => void supprimer()} loading={suppressionEnCours}>
              {suppressionEnCours
                ? t('saisies.suppression.enCours')
                : t('saisies.suppression.confirmer')}
            </Button>
          </>
        }
      />
      {elevation.dialogue}
    </div>
  );
}
